// Mirror backend camelCase field names exactly — no aliasing.
// Add types here as backend endpoints are built.

export type InvoiceDetailLevel = 'summary' | 'per_phase' | 'per_consultant' | 'per_week' | 'per_day' | 'per_entry'

export const INVOICE_DETAIL_LEVELS: { value: InvoiceDetailLevel; label: string }[] = [
  { value: 'summary',        label: 'Summary (one line total)'     },
  { value: 'per_phase',      label: 'Per phase'                    },
  { value: 'per_consultant', label: 'Per consultant'               },
  { value: 'per_week',       label: 'Per week per consultant'      },
  { value: 'per_day',        label: 'Per day per consultant'       },
  { value: 'per_entry',      label: 'Per entry (every time entry)' },
]

export interface Customer {
  id:                    number
  name:                  string
  orgNr:                 string | null
  fortnoxCustomerNumber: string | null
  billingEmail:          string | null
  paymentTerms:          string | null
  currency:              string
  website:               string | null
  street:                string | null
  city:                  string | null
  postalCode:            string | null
  country:               string
  tenantId:              string | null
  hasMsa:                boolean
  msaSignedDate:         string | null
  hasNda:                boolean
  ndaSignedDate:         string | null
  hasDpa:                boolean
  dpaSignedDate:         string | null
  devopsOrgUrl:          string | null
  teamsUrl:              string | null
  sharepointUrl:         string | null
  notes:                 string | null
  invoiceDetailLevel:    InvoiceDetailLevel
  /** Tax zone axis for the revenue-account matrix (WI-11973). null until set. */
  taxZone:               CustomerTaxZone | null
  accountManagerId:      number | null
  accountManagerName:    string | null
  isActive:              boolean
  createdAt:             string
  updatedAt:             string | null
  contactCount:          number
}

export interface Contact {
  id:         number
  customerId: number
  name:       string
  email:      string | null
  phone:      string | null
  title:      string | null
  role:       'primary' | 'technical' | 'billing' | 'stakeholder'
  isPrimary:  boolean
  notes:      string | null
  createdAt:  string
}

export interface CustomerDetail extends Customer {
  contacts: Contact[]
}

export interface CustomerSetting {
  id:                    number
  name:                  string
  fortnoxCustomerNumber: string | null
  invoiceDetailLevel:    InvoiceDetailLevel
  /** WI-11973: revenue-account matrix axis. null until set. */
  taxZone:               CustomerTaxZone | null
  isActive:              boolean
}

export interface Project {
  id:                          number
  customerId:                  number | null
  customerName:                string | null
  name:                        string
  description:                 string | null
  status:                      'active' | 'completed' | 'on_hold' | 'archived'
  createdAt:                   string
  invoiceDetailLevelOverride:  InvoiceDetailLevel | null
  fortnoxProjectNumber:        string | null
  projectManagerId:            number | null
  projectManagerName:          string | null
  /** Roll-ups returned by GET /projects (list view stats). */
  phaseCount:                  number
  resourceCount:               number
  totalHours:                  number
  /** Comma-separated full names; null when no resources are assigned. */
  resourceNames:               string | null
  /** Days since the most recent time entry on this project. null when no
   *  time has ever been logged. Returned by GET /projects only (WI-11898). */
  daysSinceWorkedOn:           number | null
  /** Worked hours summed across the current ISO week (Mon..today) for the
   *  projects-overview Dashboard reporting list (WI-11908). */
  hoursThisWeek:               number
  /** Sum of phase-resource budget hours across active phases. null when no
   *  resources have a budget set yet (WI-11908). */
  budgetHours:                 number | null
  /** WI-11973: revenue-category cascade default (CONSULTING / OFFSHORE / SERVICE_AGREEMENT). */
  revenueCategory:             ProjectRevenueCategory
  /** WI-11973: when true, every time row resolves to SERVICE_AGREEMENT. */
  isServiceAgreement:          boolean
  /** WI-11973: when false, time entries on the project are skipped at invoice generation. */
  isBillable:                  boolean
}

// WI-11973: ProjectRevenueCategory restricts RevenueCategory to the three
// values valid in the project / phase / activity cascade. The full
// RevenueCategory union and TAX_ZONES live in the Revenue Account Matrix
// section further down (defined by the matrix-admin PR).

export type ProjectRevenueCategory = Extract<RevenueCategory, 'CONSULTING' | 'OFFSHORE' | 'SERVICE_AGREEMENT'>

export const PROJECT_REVENUE_CATEGORIES: { value: ProjectRevenueCategory; label: string }[] = [
  { value: 'CONSULTING',        label: 'Consulting'        },
  { value: 'OFFSHORE',          label: 'Offshore'          },
  { value: 'SERVICE_AGREEMENT', label: 'Service Agreement' },
]

export interface ProjectHoursSummary {
  totalWorkedHrs:       number
  totalBillableHrs:     number
  entryCount:           number
  workedHrsThisMonth:   number
  billableHrsThisMonth: number
  byPhase: { phaseName: string | null; workedHrs: number; billableHrs: number }[]
}

// ─── Planning page rollups ───────────────────────────────────────────────────

export interface ActivePhase {
  id:               number
  phaseName:        string
  priority:         number
  status:           'active' | 'closed'
  startDate:        string | null
  endDate:          string | null
  projectId:        number
  projectName:      string
  customerName:     string | null
  resourceCount:    number
  totalBudgetHours: number
}

export interface MyPhase {
  id:           number
  phaseName:    string
  priority:     number
  status:       'active' | 'closed'
  startDate:    string | null
  endDate:      string | null
  projectId:    number
  projectName:  string
  customerName: string | null
  budgetHours:  number | null
}

export interface MyActivity {
  id:           number
  activityName: string
  priority:     number
  phaseId:      number
  phaseName:    string
  phaseStatus:  'active' | 'closed'
  projectId:    number
  projectName:  string
  customerName: string | null
}

export type PhaseStatus = 'active' | 'closed'

export interface Phase {
  id:                  number
  projectId:           number
  name:                string
  isDefault:           boolean
  startDate:           string | null
  endDate:             string | null
  deliveryType:        string | null
  priority:            number
  status:              PhaseStatus
  isActive:            boolean
}

// ─── Time work-tree (mobile cascade picker) ─────────────────────────────────

export interface WorkTreeActivity {
  id:   number
  name: string
}

export interface WorkTreePhaseAssignment {
  hours:       number
  hoursLogged: number
  startDate:   string | null
  endDate:     string | null
}

export interface WorkTreePhase {
  id:         number
  name:       string
  status:     PhaseStatus
  startDate:  string | null
  endDate:    string | null
  assignment: WorkTreePhaseAssignment | null
  activities: WorkTreeActivity[]
}

export interface TtEligibleArticle {
  id:   number
  name: string
}

export interface WorkTreeProject {
  id:                  number
  name:                string
  phases:              WorkTreePhase[]
  /** TT certification: true iff the calling user has exactly one eligible
   *  TT article for this project (auto-resolution unambiguous). */
  certified?:          boolean
  defaultTtArticleId?: number | null
  eligibleTtArticles?: TtEligibleArticle[]
}

export interface WorkTreeCustomer {
  id:       number | string
  name:     string
  projects: WorkTreeProject[]
}

// ─── Activities & resources (WI-11753) ───────────────────────────────────────

export interface Activity {
  id:        number
  phaseId:   number
  name:      string
  isDefault: boolean
  priority:  number
  isActive:  boolean
  createdAt: string
}

export interface PhaseResource {
  id:          number
  phaseId:     number
  personId:    number
  personName:  string
  personEmail: string | null
  budgetHours: number | null
  createdAt:   string
}

export interface ActivityResource {
  id:          number
  activityId:  number
  personId:    number
  personName:  string
  personEmail: string | null
  createdAt:   string
}

// ─── Hourly rates (WI-11801) ─────────────────────────────────────────────────

export type RateScope = 'workspace' | 'project' | 'phase' | 'activity'

export interface RateEntry {
  id:         number
  scope:      RateScope
  parentId:   number | null   // null only when scope === 'workspace'
  personId:   number | null   // null = all people at this scope
  personName: string | null
  hourlyRate: number
  validFrom:  string           // YYYY-MM-DD
  validTo:    string | null    // YYYY-MM-DD or null = open-ended
  notes:      string | null
  createdAt:  string
}

export interface Module {
  key:         string
  displayName: string
  isActive:    boolean
}

export interface CustomerModule {
  customerId:  number
  moduleKey:   string
  enabledAt:   string
  config:      Record<string, unknown> | null
}

export interface User {
  id:                 number
  entraOid:           string
  email:              string
  displayName:        string
  roles:              string | null
  fortnoxEmployeeId:  string | null
  isActive:           boolean
  // Null for users added via Entra search who have never logged in (V072)
  firstLoginAt:       string | null
  lastLoginAt:        string | null
  // joined from core.people (may be null if not linked)
  jobTitle:           string | null
  department:         string | null
  // Hailey-sourced manager chain; read-only in the Forge UI (WI-11888)
  managerId:          number | null
  managerName:        string | null
}

// One result row from GET /users/entra/search. The backend filters out hits
// that already have a Forge account so this is always a "new account" candidate.
export interface EntraUserHit {
  oid:         string
  displayName: string
  email:       string
}

export interface PersonDirectoryEntry {
  id:              number
  haileyId:        string
  firstName:       string
  lastName:        string
  email:           string | null
  jobTitle:        string | null
  department:      string | null
  employmentType:  string | null
  employmentStart: string | null
  employmentEnd:   string | null
  isActive:        boolean
  /** WI-11973: when true, time entries by this resource resolve to OFFSHORE
   *  revenue (unless the project is a Service Agreement project). */
  isOffshore:      boolean
  reportsToName:   string | null
  // Forge account (null = never logged in)
  forgeUserId:     number | null
  forgeDisplayName: string | null
  forgeRoles:      string | null
  forgeIsActive:   boolean | null
  lastLoginAt:     string | null
  // Hailey-sourced manager chain mirrored onto core.users (WI-11888)
  managerId:       number | null
  managerName:     string | null
}

export interface FortnoxArticle {
  articleNumber: string
  description:   string
  unit:          string | null
  price:         number | null
  priceList:     string | null
}

export interface FortnoxCustomer {
  id:                    number
  name:                  string
  fortnoxCustomerNumber: string | null
  isActive:              boolean
}

export interface FortnoxStatus {
  connected:        boolean
  payrollConnected: boolean
  articleCount:     number
  customerCount:    number
  projectCount:     number
  lastSync:         string | null
  invoiceCount:     number
  voucherCount:     number
  paymentCount:     number
  invoiceRowCount:  number
  accountCount:     number
}

export interface PaginatedResponse<T> {
  items:  T[]
  total:  number
  limit:  number
  offset: number
}

export interface FortnoxInvoice {
  documentNumber: string
  customerNumber: string | null
  customerName:   string | null
  total:          number | null
  totalVat:       number | null
  balance:        number | null
  currency:       string | null
  invoiceDate:    string | null
  dueDate:        string | null
  finalPayDate:   string | null
  status:         string | null
  invoiceType:    string | null
  ocr:            string | null
  project:        string | null
  yourReference:  string | null
  ourReference:   string | null
  booked:         boolean | null
  cancelled:      boolean | null
  sent:           boolean | null
  voucherNumber:  number | null
  voucherSeries:  string | null
  voucherYear:    number | null
  rowCount:       number
}

export interface FortnoxInvoiceRow {
  rowId:             number
  accountNumber:     number | null
  articleNumber:     string | null
  description:       string | null
  deliveredQuantity: number | null
  unit:              string | null
  price:             number | null
  priceExcludingVat: number | null
  discount:          number | null
  discountType:      string | null
  vat:               number | null
  total:             number | null
  totalExcludingVat: number | null
  costCenter:        string | null
  project:           string | null
}

export interface FortnoxAccount {
  number:                number
  year:                  number
  active:                boolean | null
  description:           string | null
  sru:                   number | null
  vatCode:               string | null
  balanceBroughtForward: number | null
  balanceCarriedForward: number | null
  costCenter:            string | null
  project:               string | null
}

export interface FortnoxProject {
  projectNumber:   string
  description:     string
  status:          string | null
  startDate:       string | null
  endDate:         string | null
  isActive:        boolean
  coreProjectId:   number | null
  coreProjectName: string | null
}

export interface FortnoxProject {
  projectNumber:  string
  description:    string
  status:         string | null
  startDate:      string | null
  endDate:        string | null
  projectLeader:  string | null
  isActive:       boolean
}

export interface SyncLogEntry {
  id:               number
  entity:           string
  syncedAt:         string
  recordsUpserted:  number
  status:           'success' | 'partial' | 'failed'
  errorMessage:     string | null
}

export interface ApiErrorResponse {
  error:   string
  message?: string
}

export interface HaileyStatus {
  connected:     boolean
  employeeCount: number
  lastSync:      string | null
}

export interface HaileyPerson {
  id:              number
  haileyId:        string
  firstName:       string
  lastName:        string
  email:           string | null
  jobTitle:        string | null
  department:      string | null
  employmentStart: string | null
  employmentEnd:   string | null
  employmentType:  string | null
  isActive:        boolean
  reportsToName:   string | null
}

// ─── Azure Cost ──────────────────────────────────────────────────────────────

export interface AzureCostStatus {
  configured:      boolean
  subscriptionId?: string
  rowCount?:       number
  syncedDays?:     number
  currency?:       string
  lastSyncAt?:     string | null
  lastSyncStatus?: 'running' | 'success' | 'error' | null
  lastSyncRows?:   number | null
}

export interface AzureCostSyncLogEntry {
  id:             number
  subscriptionId: string | null
  syncDate:       string | null
  startedAt:      string | null
  finishedAt:     string | null
  status:         'running' | 'success' | 'error'
  rowsUpserted:   number | null
  errorMessage:   string | null
}

// ─── TimeTracker ─────────────────────────────────────────────────────────────

export interface TimeTrackerStatus {
  connected:      boolean
  server?:        string
  database?:      string
  userCount?:     number
  customerCount?: number
  projectCount?:  number
  timeEntryCount?: number
  lastSyncAt?:    string | null
  lastSyncStatus?: string | null
  lastSyncError?:  string | null
}

export interface TimeTrackerUser {
  id:                number
  firstName:         string | null
  lastName:          string | null
  email:             string | null
  jobTitle:          string | null
  isActive:          boolean
  fortnoxEmployeeId: string | null
  employmentDate:    string | null
}

// ─── Customer ↔ Project mapping (WI-11777) ───────────────────────────────────

export interface MappingAvailableRow {
  forgeProjectId:        number
  forgeProjectName:      string
  fortnoxProjectNumber:  string | null
  currentCustomerId:     number | null
  currentCustomerName:   string | null
  suggestedCustomerId:   number
  suggestedCustomerName: string
  alreadyMatches:        boolean
}

export type MappingUnavailableReason =
  | 'no_fortnox_project_number'
  | 'no_tt_project'
  | 'tt_project_has_no_customer'
  | 'tt_customer_missing_fortnox_id'
  | 'fortnox_customer_not_in_forge'

export interface MappingUnavailableRow {
  forgeProjectId:        number
  forgeProjectName:      string
  fortnoxProjectNumber:  string | null
  currentCustomerId:     number | null
  currentCustomerName:   string | null
  reason:                MappingUnavailableReason
}

export interface CustomerProjectMappingResponse {
  mappingAvailable:   MappingAvailableRow[]
  mappingUnavailable: MappingUnavailableRow[]
}

// User → project mapping (WI-11777, second part)

export interface UserMappingAvailableRow {
  forgeProjectId:        number
  forgeProjectName:      string
  fortnoxProjectNumber:  string | null
  personId:              number
  personName:            string
  personEmail:           string | null
  personJobTitle:        string | null
  ttHours:               number
  totalActivePhases:     number
  phasesAssigned:        number
  alreadyOnAllPhases:    boolean
}

export type UserMappingUnavailableReason =
  | 'no_active_phases'
  | 'no_resolvable_users'
  | 'unknown'

export interface UserMappingUnavailableRow {
  forgeProjectId:        number
  forgeProjectName:      string
  fortnoxProjectNumber:  string | null
  ttUserCount:           number
  resolvedUserCount:     number
  activePhaseCount:      number
  reason:                UserMappingUnavailableReason
}

export interface UserProjectMappingResponse {
  mappingAvailable:   UserMappingAvailableRow[]
  mappingUnavailable: UserMappingUnavailableRow[]
}

// Resource price import (WI-11778)

export interface PriceMappingAvailableRow {
  fortnoxProjectCode: string | null
  forgeProjectId:     number
  forgeProjectName:   string
  personId:           number
  personName:         string
  personEmail:        string | null
  articleId:          number
  articleCode:        string | null
  articleName:        string | null
  pricePerHour:       number | null
  validFrom:          string | null
  validTo:            string | null
  billableHours:      number | null
  defaultPhaseId:     number
  alreadyImported:    boolean
}

export type PriceMappingUnavailableReason =
  | 'forge_project_not_found'
  | 'forge_person_not_found'
  | 'no_default_phase'
  | 'unknown'

export interface PriceMappingUnavailableRow {
  fortnoxProjectCode: string | null
  forgeProjectId:     number | null
  forgeProjectName:   string | null
  personEmail:        string | null
  personId:           number | null
  articleCount:       number
  billableHours:      number | null
  reason:             PriceMappingUnavailableReason
}

export interface ResourcePriceMappingResponse {
  mappingAvailable:   PriceMappingAvailableRow[]
  mappingUnavailable: PriceMappingUnavailableRow[]
}

// ─── TimeTracker registered-time import (WI-11808) ───────────────────────────

export interface TimeMappingAvailableRow {
  fortnoxProjectCode:     string | null
  forgeProjectId:         number
  forgeProjectName:       string
  personId:               number
  personName:             string
  personEmail:            string | null
  staffId:                number
  defaultPhaseId:         number
  entriesTotal:           number
  entriesImported:        number
  entriesMissingActivity: number
  entriesPending:         number
  hoursTotal:             number
  hoursPending:           number
  dateFrom:               string | null
  dateTo:                 string | null
}

export type TimeMappingUnavailableReason =
  | 'no_fortnox_code'
  | 'forge_project_not_found'
  | 'forge_person_not_found'
  | 'no_forge_user'
  | 'no_default_phase'
  | 'unknown'

export interface TimeMappingUnavailableRow {
  fortnoxProjectCode: string | null
  forgeProjectId:     number | null
  forgeProjectName:   string | null
  personEmail:        string | null
  personId:           number | null
  entriesTotal:       number
  hoursTotal:         number | null
  dateFrom:           string | null
  dateTo:             string | null
  reason:             TimeMappingUnavailableReason
}

export interface TimeMappingResponse {
  mappingAvailable:   TimeMappingAvailableRow[]
  mappingUnavailable: TimeMappingUnavailableRow[]
}

// ─── My Time dashboard (WI-11822) ─────────────────────────────────────────────

export interface MyDashboardKpiToday   { hours: number; target: number; remaining: number; pct: number; revenue: number }
export interface MyDashboardKpiWeek    { hours: number; target: number; pct: number; paceDelta: number; workingDay: number; revenue: number }
export interface MyDashboardKpiMonth   { hours: number; target: number; pct: number; dayOfMonth: number; daysInMonth: number; revenue: number }
export interface MyDashboardKpiBillable { sharePct: number; targetPct: number; last30Worked: number; last30Billable: number }

export interface MyDashboardKpis {
  today:    MyDashboardKpiToday
  week:     MyDashboardKpiWeek
  month:    MyDashboardKpiMonth
  billable: MyDashboardKpiBillable
}

export interface ProductionRateRow {
  entryDate:    string
  customerId:   number | null
  customerName: string
  hours:        number
}

export interface WhereTimeGoesRow {
  customerId:   number | null
  customerName: string
  hours:        number
  sharePct:     number
}

export interface ComplianceDay {
  date:  string
  hours: number
}

export interface ByActivityRow {
  activityId:   number | null
  activityName: string
  hours:        number
  sharePct:     number
}

export type PlannedWorkStatus = 'over' | 'behind' | 'ahead' | 'ontrack' | 'ongoing'

export interface PlannedWorkRow {
  phaseResourceId: number
  phaseId:         number
  phaseName:       string
  projectId:       number
  projectName:     string
  customerId:      number | null
  customerName:    string | null
  phaseStatus:     string
  startDate:       string | null
  endDate:         string | null
  budgetHours:     number
  loggedHours:     number
  expectedPct:     number | null
  status:          PlannedWorkStatus
  deltaHours:      number
}

export interface AdoDashboardTask {
  id:            number
  title:         string
  workItemType:  string
  state:         string
  areaPath:      string | null
  iterationPath: string | null
  projectName:   string | null
}

export interface ReportingMissingDay {
  date:         string
  hoursLogged:  number
  hoursMissing: number
}

export interface MyDashboardResponse {
  me: {
    displayName: string
    firstName:   string
    email:       string
    staffId:     number
    personId:    number | null
  }
  today:      string
  weekStart:  string
  monthStart: string
  monthEnd:   string
  weekNumber: number
  kpis:       MyDashboardKpis
  productionRate: ProductionRateRow[]
  whereTimeGoes:  { totalHours: number; rows: WhereTimeGoesRow[] }
  compliance:     { days: ComplianceDay[] }
  byActivity:     ByActivityRow[]
  plannedWork:    PlannedWorkRow[]
  adoTasks:       AdoDashboardTask[]
  reportingAlert: {
    missingDays:    ReportingMissingDay[]
    lastSubmission: string | null
  }
}

// ─── Time Reporting ───────────────────────────────────────────────────────────

export interface TimeEntryMergeResponse {
  mergedId:         number
  sourcesDeleted:   number
  workedHrs:        number
  billableHrs:      number
  notesTruncated:   boolean
  invoiceTruncated: boolean
}

export interface LeaveType {
  code:      string
  label:     string
  sortOrder: number
}

export interface TimeEntry {
  id:                 number
  staffId:            number
  /** Display name of the staff member who logged the entry. Always returned
   *  by GET /time/entries (WI-11888). Useful when scope != 'mine'. */
  staffName:          string | null
  projectId:          number | null
  projectName:        string | null
  customerId:         number | null
  customerName:       string | null
  phaseId:            number | null
  phaseName:          string | null
  activityId:         number | null
  activityName:       string | null
  /** Legacy column kept for migration data; null for new project entries.
   *  Leave entries store the leave-type code here (used by payroll sync). */
  articleNumber:      string | null
  articleDescription: string | null
  articlePrice:       number | null
  leaveLabel:         string | null   // non-null when entry is a leave type
  entryDate:          string          // YYYY-MM-DD
  workedHrs:          number
  billableHrs:        number
  invoiceText:        string | null
  notes:              string | null
  /** TimeTracker outbound sync state (WI-11887). ttReportedTimeId is the id
   *  in dbo.ReportedTime once the push succeeded; ttSyncStatus is non-null
   *  only when a push failed and is queued for retry. */
  ttReportedTimeId:   number | null
  ttArticleId:        number | null
  ttSyncStatus:       'failed' | null
}

export type WeekStatusValue = 'draft' | 'submitted' | 'approved' | 'rejected'

export interface WeekStatus {
  id:             number | null
  weekStart:      string           // YYYY-MM-DD
  status:         WeekStatusValue
  submittedAt:    string | null
  approvedByName: string | null
  approvedAt:     string | null
  rejectionNote:  string | null
  invoicedAt:     string | null
  fortnoxInvRef:  string | null
}

export interface ApprovalRow {
  weekId:          number
  staffId:         number
  staffName:       string
  weekStart:       string
  status:          WeekStatusValue
  workedHrs:       number
  billableHrs:     number
  submittedAt:     string | null
  invoicedAt:      string | null
  payrollSyncedAt: string | null
}

export interface PayrollSyncResult {
  synced:  number
  skipped: number
  errors:  string[]
}

export interface UtilisationRow {
  staffId:     number
  staffName:   string
  workedHrs:   number
  billableHrs: number
  targetHrs:   number
  department:  string | null
}

export interface AvailableHoursEntry {
  date:           string  // YYYY-MM-DD
  availableHours: number
}

export interface PublicHoliday {
  id:          number
  date:        string
  countryCode: string
  name:        string
  description: string | null
}

export interface CalendarificStatus {
  configured: boolean
}

// ─── Finance / Budget tracking (WI-11881) ────────────────────────────────────

export interface ForecastVersion {
  id:               number
  name:             string
  isBudget:         boolean
  isCurrentDefault: boolean
  forecastStart:    string | null
  forecastEnd:      string | null
  isActive:         boolean
}

export interface ActiveForecastVersion {
  activeVersionId:   number | null
  activeVersionName: string | null
  isBudget:          boolean
}

export interface BudgetMonth {
  month:               string    // 'YYYY-MM'
  budgetAmount:        number
  workingDays:         number
  budgetPerWorkingDay: number
  reportedRevenue:     number
  accumulatedBudget:   number
  accumulatedRevenue:  number
}

export interface MyBudgetResponse {
  configured:      boolean
  costCenter:      string | null
  versionId:       number | null
  versionName:     string | null
  isBudget:        boolean
  months:          BudgetMonth[]
  ytdBudget:       number
  ytdRevenue:      number
  ytdBudgetToDate: number
  ytdVariance:     number
  message?:        string
}

// ─── Clementine ──────────────────────────────────────────────────────────────

export interface ClementineStatus {
  configured:            boolean
  lastSyncAt?:           string | null
  lastSyncStatus?:       string | null
  forecastVersionCount?: number
  forecastCount?:        number
  costCenterCount?:      number
  resultSheetCount?:     number
}

export interface ClementineSyncLogEntry {
  entity:          string
  recordsUpserted: number
  status:          string
  errorMessage:    string | null
  syncedAt:        string | null
}

export interface CalendarificImportResult {
  year:        number
  countryCode: string
  imported:    number
  skipped:     number
  errors:      number
  total:       number
}

// ─── Sales ───────────────────────────────────────────────────────────────────

export interface Opportunity {
  id:             number
  name:           string
  account:        string
  value:          number
  stage:          string
  closeAt:        string | null
  ownerId:        number | null
  ownerName:      string | null
  ownerEntraOid:  string | null
  health:         'on-track' | 'at-risk' | 'won'
  lastActivity:   string | null
  lastActivityAt: string | null
  ageInStage:     number
  notes:          string | null
  winLossReason:  string | null
  createdAt:      string
  updatedAt:      string | null
}

// ─── Contracts ────────────────────────────────────────────────────────────────

export type ContractStatus = 'draft' | 'active' | 'renewal-due' | 'expired'
export type ContractType   = 'msa' | 'nda' | 'dpa' | 'sow' | 'service' | 'framework' | 'other'

export const CONTRACT_TYPES: { value: ContractType; label: string }[] = [
  { value: 'service',   label: 'Service Agreement'               },
  { value: 'msa',       label: 'Master Service Agreement (MSA)'  },
  { value: 'sow',       label: 'Statement of Work (SOW)'         },
  { value: 'nda',       label: 'Non-Disclosure Agreement (NDA)'  },
  { value: 'dpa',       label: 'Data Processing Agreement (DPA)' },
  { value: 'framework', label: 'Framework Agreement'             },
  { value: 'other',     label: 'Other'                           },
]

export interface Agreement {
  id:              number
  customerId:      number
  customerName:    string | null
  contractType:    ContractType
  title:           string
  referenceNumber: string | null
  signedDate:      string | null
  startDate:       string | null
  endDate:         string | null
  value:           number | null
  currency:        string
  mrr:             number | null
  status:          ContractStatus
  renewalDays:     number | null
  ownerId:         number | null
  ownerName:       string | null
  documentUrl:     string | null
  notes:           string | null
  createdAt:       string
  updatedAt:       string | null
}

// ─── Invoicing ────────────────────────────────────────────────────────────────

export interface RawEntry {
  id:                 number
  entryDate:          string
  staffName:          string | null
  phaseName:          string | null
  activityName:       string | null
  articleNumber:      string | null
  articleDescription: string | null
  billableHrs:        number
  invoiceText:        string | null
  rate:               number
}

export interface InvoiceEntriesResponse {
  projectName: string
  detailLevel: InvoiceDetailLevel
  entries:     RawEntry[]
}

export interface InvoiceLine {
  description:   string
  qty:           number
  unitPrice:     number
  amount:        number
  articleNumber: string
}

export interface InvoicePreview {
  projectName:  string
  detailLevel:  InvoiceDetailLevel
  from:         string
  to:           string
  totalHrs:     number
  totalAmount:  number
  lines:        InvoiceLine[]
}

// ─── Forge-side invoices (WI-11974) ──────────────────────────────────────────

export type InvoiceStatus =
  | 'draft'
  | 'ready'
  | 'pushing'
  | 'pushed'
  | 'push_failed'
  | 'cancelled_in_fortnox'
  | 'superseded'
  | 'discarded'

export type InvoiceRowType = 'TIME' | 'MANUAL'

export type RevenueCategoryResolutionReason =
  | 'SERVICE_AGREEMENT_PROJECT'
  | 'OFFSHORE_RESOURCE'
  | 'ACTIVITY_OVERRIDE'
  | 'PHASE_OVERRIDE'
  | 'PROJECT_DEFAULT'
  | 'SYSTEM_DEFAULT'
  | 'MANUAL'

export interface ForgeInvoice {
  id:                    number
  draftNumber:           string
  customerId:            number
  customerName:          string
  periodStart:           string
  periodEnd:             string
  invoiceDate:           string
  currency:              string
  status:                InvoiceStatus
  fortnoxInvoiceNumber:  string | null
  fortnoxPushedAt:       string | null
  supersededByInvoiceId: number | null
  notes:                 string | null
  createdAt:             string
  createdBy:             number | null
  updatedAt:             string
  updatedBy:             number | null
}

export interface ForgeInvoiceRow {
  id:                                 number
  invoiceId:                          number
  rowOrder:                           number
  description:                        string
  quantity:                           number
  unit:                               string
  unitPrice:                          number
  lineTotal:                          number
  vatRate:                            number
  rowType:                            InvoiceRowType
  projectId:                          number | null
  personId:                           number | null
  activityId:                         number | null
  resolvedRevenueCategory:            RevenueCategory
  fortnoxAccountNumber:               number
  revenueCategoryResolutionReason:    RevenueCategoryResolutionReason
  fortnoxRowNumber:                   number | null
  createdAt:                          string
  updatedAt:                          string
}

export interface ForgeInvoiceDetail extends ForgeInvoice {
  rows: ForgeInvoiceRow[]
}

// ─── Revenue Account Matrix (WI-11973) ───────────────────────────────────────

export type RevenueCategory =
  | 'CONSULTING'
  | 'OFFSHORE'
  | 'SERVICE_AGREEMENT'
  | 'LICENSE_RESELLING'
  | 'PRODUCT'

export type CustomerTaxZone = 'SE' | 'EU' | 'NON_EU'

export const REVENUE_CATEGORIES: { value: RevenueCategory; label: string; description: string }[] = [
  { value: 'CONSULTING',        label: 'Consulting',        description: 'Default — billable hours by Numberskills consultants' },
  { value: 'OFFSHORE',          label: 'Offshore',          description: 'Hours delivered by subcontractor resources (Dhaka)'   },
  { value: 'SERVICE_AGREEMENT', label: 'Service Agreement', description: 'Hours on projects covered by a service agreement'     },
  { value: 'LICENSE_RESELLING', label: 'License Reselling', description: 'Manual non-time rows for resold licenses'             },
  { value: 'PRODUCT',           label: 'Product',           description: 'Manual non-time rows for Numberskills products'       },
]

export const TAX_ZONES: { value: CustomerTaxZone; label: string }[] = [
  { value: 'SE',     label: 'Sweden'    },
  { value: 'EU',     label: 'EU'        },
  { value: 'NON_EU', label: 'Non-EU'    },
]

export interface RevenueAccountMatrixEntry {
  id:                   number
  revenueCategory:      RevenueCategory
  customerTaxZone:      CustomerTaxZone
  fortnoxAccountNumber: number
  description:          string | null
  createdAt:            string
  updatedAt:            string | null
}

export interface RevenueAccountOption {
  number:      number
  description: string | null
}

// ─── Monitoring ───────────────────────────────────────────────────────────────

export type MonitoringHealthStatus = 'healthy' | 'degraded' | 'critical' | 'idle'

export interface MonitoringCustomerHealth {
  tenant_id:        string
  customer_name:    string | null
  onboarding_status: string | null
  workspace_count:  number
  item_count:       number
  runs_24h:         number
  failed_24h:       number
  running_now:      number
  success_24h:      number
  last_run_at:      string | null
  last_failed_at:   string | null
  last_success_at:  string | null
  healthStatus:     MonitoringHealthStatus
  successRate:      number | null
}

export interface MonitoringStats {
  runs24h:      number
  failed24h:    number
  running:      number
  success24h:   number
  successRate:  number
  p95Seconds:   number | null
  p95Formatted: string
}

export type MonitoringRunStatus = 'success' | 'failed' | 'running' | 'warning' | 'unknown'

export interface MonitoringRun {
  id:                number | string
  customer_id:       string | null
  customer_name:     string | null
  workspace_name:    string
  item_name:         string | null
  execution_type:    string | null
  event_type:        string | null
  status:            string
  statusNorm:        MonitoringRunStatus
  duration_seconds:  number | null
  durationFormatted: string
  event_timestamp:   string
  error_message:     string | null
}

export interface MonitoringInventoryCard {
  tenant_id:         string
  customer_name:     string | null
  workspace_name:    string
  capacity_sku:      string | null
  total_items:       number
  healthy:           number
  failing:           number
  warning:           number
  last_discovered_at: string | null
}

export interface MonitoringAlertRule {
  id:               number
  customer_id:      string | null
  customer_name:    string | null
  alert_type:       string | null
  workspace_pattern: string | null
  alert_channel:    string | null
  channel_target:   string | null
  isEnabled:        boolean
  created_at:       string | null
}

export interface MonitoringApimSubscription {
  id:               number
  subscriptionName: string | null
  keyMasked:        string
  tier:             string | null
  requestsPerHour:  number | null
  requestsPerDay:   number | null
  totalRequests:    number | null
  lastUsedAt:       string | null
  createdAt:        string | null
}

export interface MonitoringRecentEvent {
  id:                number | string
  workspace_name:    string
  item_name:         string | null
  execution_type:    string | null
  status:            string
  statusNorm:        MonitoringRunStatus
  duration_seconds:  number | null
  durationFormatted: string
  event_timestamp:   string
  error_message:     string | null
}

export interface MonitoringFabricWorkspace {
  workspace_id:   string
  workspace_name: string
  capacity_id:    string | null
  region:         string | null
  created_at:     string | null
}

export interface MonitoringCustomerTab {
  enrolled:         boolean
  tenantId:         string | null
  customerName:     string | null
  contractType:     string | null
  onboardingStatus: string | null
  onboardedAt:      string | null
  apim:             MonitoringApimSubscription | null
  events30d:        { total: number; failed: number; success: number } | null
  recentEvents:     MonitoringRecentEvent[]
  alertRules:       MonitoringAlertRule[]
  workspaces:       MonitoringFabricWorkspace[]
}

// ─── Pipeline Schedule Gantt ──────────────────────────────────────────────────

export interface GanttRun {
  startedAt:   string          // DB datetime string (UTC, no TZ suffix)
  durationSec: number | null
  status:      'success' | 'failed' | 'running' | 'pending'
}

export interface GanttPipeline {
  customerId:        string
  customerName:      string | null
  workspaceName:     string
  pipelineName:      string
  scheduleFrequency: string | null
  scheduleDetails:   string | null
  histMinSec:        number | null
  histAvgSec:        number | null
  histMaxSec:        number | null
  histSamples:       number
  runsToday:         GanttRun[]
}

export interface GanttData {
  generatedAt: string
  pipelines:   GanttPipeline[]
}

// ─── Leads ────────────────────────────────────────────────────────────────────

export type IntegrationStatus = 'none' | 'partial' | 'full'

export interface SourceSystem {
  id:                number
  name:              string
  integrationStatus: IntegrationStatus
  isCloud:           boolean
}

export interface ProspectSourceSystem extends SourceSystem {
  prospectSystemId: number
}

export interface ProspectContact {
  id:          number
  prospectId:  number
  name:        string
  role:        string | null
  email:       string | null
  phone:       string | null
  linkedinUrl: string | null
  createdAt:   string
}

export interface CompanyStructureNode {
  id:         string
  name:       string
  flag:       string
  percentage: number | null
  parentId:   string | null
}

export interface Prospect {
  id:                  number
  companyId:           string
  companyName:         string
  legalGroupCode:      string | null
  legalGroupText:      string | null
  town:                string | null
  roaringFetchedAt:    string
  phase:               string
  subPhase:            string
  companiesInScope:    number | null
  hasBudgetForecast:   boolean
  notes:               string | null
  contactCount:        number
  contacts?:           ProspectContact[]
  sourceSystems?:      ProspectSourceSystem[]
  companyStructure:    CompanyStructureNode[] | null
  arrValue:            number | null
  createdAt:           string
  updatedAt:           string | null
  clementineFitScore:  number | null
  latestResearchId:    number | null
  lastResearchedAt:    string | null
}

export interface LeadResearchContact {
  id:          number
  researchId:  number
  position:    number
  name:        string
  title:       string | null
  email:       string | null
  phone:       string | null
  linkedinUrl: string | null
  note:        string | null
  sourceUrl:   string | null
}

export interface LeadResearchFinancial {
  id:                         number
  researchId:                 number
  entityOrgNumber:            string | null
  year:                       number
  revenueKsek:                number | null
  ebitKsek:                   number | null
  resultAfterFinancialKsek:   number | null
  ebitMarginPct:              number | null
  profitMarginPct:            number | null
  growthPct:                  number | null
  isApproximate:              boolean
  note:                       string | null
  sourceUrl:                  string | null
}

export interface LeadResearchNews {
  id:             number
  researchId:     number
  publishedDate:  string | null
  publishedLabel: string | null
  sourceName:     string | null
  headline:       string
  url:            string | null
  newsType:       string | null
  sentiment:      'positive' | 'negative' | 'neutral' | 'mixed' | null
  relevance:      'HIGH' | 'MEDIUM' | 'LOW' | null
  summary:        string | null
}

export interface LeadResearch {
  id:                     number
  prospectId:             number
  orgNumber:              string | null
  legalName:              string | null
  tradingName:            string | null
  website:                string | null
  linkedinCompanyUrl:     string | null
  hqStreet:               string | null
  hqPostalCode:           string | null
  hqCity:                 string | null
  hqCountry:              string | null
  industrySni:            string | null
  companyDescription:     string | null
  foundedYear:            number | null
  switchboardPhone:       string | null
  generalEmail:           string | null
  contactUsUrl:           string | null
  ceoName:                string | null
  ceoEmail:               string | null
  ceoPhone:               string | null
  ceoNote:                string | null
  cfoName:                string | null
  cfoEmail:               string | null
  cfoPhone:               string | null
  cfoNote:                string | null
  employeesCompany:       number | null
  employeesCompanyYear:   number | null
  employeesGroupNote:     string | null
  employeesTrend:         string | null
  ultimateParent:         string | null
  intermediateParent:     string | null
  groupRole:              string | null
  subsidiariesCount:      number | null
  groupSizeCompanies:     number | null
  relatedEntities:        Array<{ orgNumber: string | null; name: string; role: string; note?: string }>
  erpSignals:             string[]
  clementineFitScore:     number | null
  clementineFitReasoning: string | null
  recommendedApproach:    string | null
  status:                 'success' | 'validation_failed' | 'api_error' | 'partial'
  promptVersion:          string
  model:                  string
  costMetadata:           { inputTokens: number; outputTokens: number; webSearches: number; usdCost: number } | null
  confidenceNotes:        Record<string, string> | null
  sources:                string[]
  createdAt:              string
  createdById:            number | null
  createdByName:          string | null
  contacts:               LeadResearchContact[]
  financials:             LeadResearchFinancial[]
  news:                   LeadResearchNews[]
}

// ─── Azure Cost ──────────────────────────────────────────────────────────────

export interface CostTrend { absChange: number; pctChange: number }

export interface CostService {
  name:  string
  total: number
  daily: number[]
}

export interface CostResourceGroup {
  name:     string
  total:    number
  daily:    number[]
  services: string[]
  trend:    CostTrend
}

export interface CostResource {
  id:       string
  name:     string
  rg:       string
  service:  string
  location: string
  total:    number
  daily:    number[]
  trend:    CostTrend
}

export interface CostMeta {
  subscriptionId: string
  dates:          string[]
  currency:       string
  total:          number
  rowCount:       number
  resourceCount:  number
  rgCount:        number
  lastSyncAt:     string | null
}

export interface CostDashboard {
  meta:           CostMeta
  daily:          number[]
  services:       CostService[]
  resourceGroups: CostResourceGroup[]
  resources:      CostResource[]
}

export interface RoaringHit {
  companyId:      string
  companyName:    string
  legalGroupCode: string
  legalGroupText: string
  town:           string
  alreadySaved?:  boolean
}

export interface RoaringSearchResult {
  hitCount: number
  hits:     RoaringHit[]
}

// ─── WI-12031: Product billing & subscriptions ───────────────────────────────

export type SubscriptionStatus      = 'PENDING' | 'ACTIVE' | 'PAUSED' | 'CANCELLED'
export type SubscriptionBillingCycle = 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
export type SubscriptionBillingMode  = 'ARREARS' | 'ADVANCE'
export type SubscriptionInvoiceGrouping = 'ONE_INVOICE' | 'SEPARATE_PER_PRODUCT_TYPE'
export type SubscriptionLineStatus   = 'ACTIVE' | 'ENDED'
export type DiscountRuleType         = 'VOLUME_TIER' | 'PERCENTAGE' | 'FIXED_AMOUNT'
export type ReconciliationStatus     = 'IN_SYNC' | 'BILLED_NOT_DELIVERED' | 'DELIVERED_NOT_BILLED'

export interface Subscription {
  id:                  number
  customerId:          number
  customerName:        string
  name:                string
  contractReference:   string | null
  poNumber:            string | null
  commitmentStart:     string
  commitmentEnd:       string | null
  renewalDate:         string | null
  noticePeriodDays:    number | null
  billingCycle:        SubscriptionBillingCycle
  billingMode:         SubscriptionBillingMode
  currency:            string
  paymentTermsDays:    number | null
  trialEndDate:        string | null
  status:              SubscriptionStatus
  invoiceGrouping:     SubscriptionInvoiceGrouping
  notes:               string | null
  createdAt:           string
  /** Roll-ups only present on the list endpoint. */
  activeLineCount?:    number
  endedLineCount?:     number
}

export interface SubscriptionLine {
  id:                          number
  subscriptionId:              number
  analyzedCompanyId:           number
  analyzedCompanyName:         string
  analyzedCompanyExternalId:   string
  productArticleId:            number
  articleNumber:               string
  articleName:                 string
  productType:                 string | null
  validFrom:                   string
  validTo:                     string | null
  trialEndDate:                string | null
  status:                      SubscriptionLineStatus
  createdAt:                   string
  updatedAt:                   string
  lastChangedBy:               string | null
  currentUnitPrice:            number | null
}

export interface SubscriptionDiscount {
  id:                    number
  subscriptionId:        number
  discountRuleId:        number
  ruleName:              string
  ruleType:              DiscountRuleType
  flatPercent:           number | null
  flatAmount:            number | null
  flatAmountCurrency:    string | null
  subscriptionLineId:    number | null
  productArticleId:      number | null
  productType:           string | null
  validFrom:             string
  validTo:               string | null
}

export interface DiscountTierBand {
  discountRuleId:     number
  fromCount:          number
  toCountInclusive:   number | null
  discountPercent:    number
  bandOrder:          number
}

export interface DiscountRule {
  id:                  number
  name:                string
  description:         string | null
  ruleType:            DiscountRuleType
  flatPercent:         number | null
  flatAmount:          number | null
  flatAmountCurrency:  string | null
  archivedAt:          string | null
  createdAt:           string
  usedByCount:         number
  tierBands:           DiscountTierBand[]
}

export interface ProductArticle {
  id:                  number
  articleNumber:       string
  name:                string
  unit:                string | null
  salesPrice:          number | null
  isActiveInFortnox:   boolean
  isProductArticle:    boolean
  productType:         string | null
  activeLineCount:     number
}

export interface ProductTypeUsage {
  code:                string
  displayName:         string
  description:         string | null
  sortOrder:           number
  archivedAt:          string | null
  articleCount:        number
  activeLineCount:     number
}

export interface AnalyzedCompany {
  id:                  number
  externalIdentifier:  string
  name:                string
  country:             string | null
}

export interface ReconciliationRow {
  customerId:                  number | null
  customerName:                string | null
  analyzedCompanyId:           number | null
  analyzedCompanyName:         string | null
  analyzedCompanyExternalId:   string | null
  productArticleId:            number | null
  productType:                 string | null
  articleName:                 string | null
  subscriptionLineId:          number | null
  isBilled:                    boolean
  isDelivered:                 boolean
  status:                      ReconciliationStatus
  acknowledgmentId:            number | null
  acknowledgmentReason:        string | null
  acknowledgmentValidUntil:    string | null
  acknowledgedAt:              string | null
  acknowledgedByName:          string | null
}

export interface ReconciliationResponse {
  summary: Record<ReconciliationStatus | string, { rowCount: number; customerCount: number }>
  rows:    ReconciliationRow[]
}

// ─── WI-12031 PR 3: subscription event audit log ─────────────────────────────

export type SubscriptionEventType =
  | 'SUBSCRIPTION_CREATED'
  | 'SUBSCRIPTION_ACTIVATED'
  | 'SUBSCRIPTION_PAUSED'
  | 'SUBSCRIPTION_RESUMED'
  | 'SUBSCRIPTION_CANCELLED'
  | 'LINE_ADDED'
  | 'LINES_BULK_ADDED'
  | 'LINE_PRICE_CHANGED'
  | 'LINE_ENDED'
  | 'LINE_TRIAL_SET'
  | 'DISCOUNT_ATTACHED'
  | 'DISCOUNT_UPDATED'
  | 'DISCOUNT_ENDED'
  | 'PAUSE_RECORDED'
  | 'PAUSE_ENDED'

/** Payload shape varies by eventType — see backend/billing/events.py for the
 * per-type contract. Common keys used by the timeline renderer:
 *  - LINE_*           → analyzedCompanyName, articleNumber, unitPrice, validFrom/To
 *  - LINE_PRICE_CHANGED → oldUnitPrice, newUnitPrice, changeReason
 *  - DISCOUNT_ATTACHED  → ruleName, ruleType
 *  - SUBSCRIPTION_*     → from, to (status transitions)
 */
export type SubscriptionEventPayload = Record<string, unknown> | null

export interface SubscriptionEvent {
  id:                  number
  subscriptionId:      number
  subscriptionLineId:  number | null
  eventType:           SubscriptionEventType
  payload:             SubscriptionEventPayload
  note:                string | null
  createdAt:           string
  createdBy:           number | null
  createdByName:       string | null
}

// ─── WI-12031 PR 4: product invoice draft generation ─────────────────────────

export interface InvoiceGenerationIssue {
  code:    string
  message: string
  fixUrl?: string
  context?: Record<string, unknown>
}

export interface ProductInvoiceDraftRow {
  rowType:                   'SUBSCRIPTION' | 'DISCOUNT'
  description:               string
  quantity:                  number
  unitPrice:                 number
  amount:                    number
  fortnoxAccountNumber:      number
  resolvedRevenueCategory:   string
  isDiscountRow:             boolean
  subscriptionId:            number | null
  subscriptionLineId:        number | null
  analyzedCompanyId:         number | null
  trialDays:                 number
  proRatedQuantity:          number | null
  adjustmentPeriodStart:     string | null
  adjustmentPeriodEnd:       string | null
  discountRuleId:            number | null
}

export interface ProductInvoiceDraft {
  customerId:     number
  periodStart:    string
  periodEnd:      string
  currency:       string
  notes:          string
  productType:    string | null
  grossTotal:     number
  discountTotal:  number
  netTotal:       number
  rows:           ProductInvoiceDraftRow[]
}

export interface ProductInvoicePreviewResponse {
  drafts: ProductInvoiceDraft[]
}

export interface ProductInvoiceCreateResponse {
  created: {
    invoiceId:     number
    draftNumber:   string
    productType:   string | null
    grossTotal:    number
    discountTotal: number
    netTotal:      number
    rowCount:      number
  }[]
}

// ─── WI-12031 PR 5: line invoice-history drawer ──────────────────────────────

/** Every invoice (active OR unlinked) this subscription line has ever appeared on,
 *  joined through `core.invoice_row_subscription_line_periods`. unlinkedAt = null
 *  means the row is currently billed; non-null means the parent invoice was
 *  cancelled / superseded / discarded and the link was soft-unlinked. */
export interface SubscriptionLineInvoiceHistoryRow {
  junctionId:            number
  invoiceRowId:          number
  invoiceId:             number
  draftNumber:           string
  fortnoxInvoiceNumber:  string | null
  invoiceStatus:         string
  invoiceDate:           string
  periodOverlapStart:    string
  periodOverlapEnd:      string
  billedDays:            number
  preDiscountAmount:     number
  lineTotal:             number
  unlinkedAt:            string | null
  unlinkReason:          string | null
}

// ─── WI-12031 PR 6: billing reports ─────────────────────────────────────────

export interface MrrReportResponse {
  asOf:    string
  total:   number
  byCustomer: {
    customerId: number
    name:       string
    mrr:        number
    lineCount:  number
  }[]
  byProductType: {
    productType: string
    mrr:         number
    lineCount:   number
  }[]
}

export interface SubscriptionChangesReportResponse {
  periodStart: string
  periodEnd:   string
  total:       number
  byEventType: {
    eventType: SubscriptionEventType | string
    count:     number
  }[]
}

export interface ExpansionContractionReportResponse {
  periodStart:  string
  periodEnd:    string
  expansion:    number
  contraction:  number
  netDelta:     number
  byEventType: {
    eventType:   SubscriptionEventType | string
    expansion:   number
    contraction: number
    count:       number
  }[]
}
